import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Check, Carrot } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { SortableHeader, sortData } from "../components/SortableHeader";
import ImportButton from "../components/ImportButton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EINHEITEN = [
  "kg",
  "g",
  "l",
  "ml",
  "Stück",
  "Bund",
  "Packung",
  "Dose",
  "Glas"
];

export const Zutaten = () => {
  const [zutaten, setZutaten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingZutat, setEditingZutat] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    einheit: "kg",
    preis_pro_einheit: "",
    lieferant: ""
  });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/zutaten`);
      setZutaten(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Zutaten");
    } finally {
      setLoading(false);
    }
  }, []);

  const resetForm = () => {
    setFormData({
      name: "",
      einheit: "kg",
      preis_pro_einheit: "",
      lieferant: ""
    });
    setEditingZutat(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (zutat) => {
    setEditingZutat(zutat);
    setFormData({
      name: zutat.name,
      einheit: zutat.einheit,
      preis_pro_einheit: zutat.preis_pro_einheit.toString(),
      lieferant: zutat.lieferant || ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.preis_pro_einheit) {
      toast.error("Bitte Name und Preis angeben");
      return;
    }

    try {
      const payload = {
        ...formData,
        preis_pro_einheit: parseFloat(formData.preis_pro_einheit)
      };

      if (editingZutat) {
        await axios.put(`${API}/zutaten/${editingZutat.id}`, payload);
        toast.success("Zutat aktualisiert");
      } else {
        await axios.post(`${API}/zutaten`, payload);
        toast.success("Zutat erstellt");
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Zutat wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/zutaten/${id}`);
      toast.success("Zutat gelöscht");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-[#858585]">Laden...</div>
      </div>
    );
  }

  return (
    <div data-testid="zutaten-page">
      {/* Header */}
      <header className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Zutaten</h1>
            <p className="page-subtitle">Pflegen Sie Ihre Zutaten für die Speisekalkulation</p>
          </div>
          <Button 
            onClick={openCreateDialog}
            className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm uppercase text-xs tracking-wide btn-press"
            data-testid="add-zutat-btn"
          >
            <Plus size={16} className="mr-2" />
            Neue Zutat
          </Button>
          <ImportButton kategorie="zutaten" felder={["name","einheit","preis_pro_einheit","lieferant"]} onImportSuccess={fetchData} />
        </div>
      </header>

      {/* Info Box */}
      <div className="px-8 py-4">
        <div className="bg-[#E3F2FD] border border-[#BBDEFB] p-4">
          <p className="text-sm text-[#1565C0]">
            <strong>Tipp:</strong> Pflegen Sie hier alle Zutaten mit aktuellen Einkaufspreisen. 
            Diese können Sie dann bei der Speisenkalkulation verwenden, um den Wareneinsatz automatisch zu berechnen.
          </p>
        </div>
      </div>

      {/* Content */}
      {zutaten.length === 0 ? (
        <div className="empty-state">
          <Carrot size={64} className="mx-auto text-[#D1CFC9]" />
          <h3>Keine Zutaten vorhanden</h3>
          <p>Erstellen Sie Zutaten für Ihre Speisekalkulation</p>
          <Button 
            onClick={openCreateDialog}
            className="mt-4 bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm"
            data-testid="add-zutat-empty-btn"
          >
            <Plus size={16} className="mr-2" />
            Erste Zutat erstellen
          </Button>
        </div>
      ) : (
        <div className="data-table-container" data-testid="zutaten-table">
          <table className="data-table">
            <thead>
              <tr>
                <SortableHeader label="Name" sortKey="name" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Einheit" sortKey="einheit" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Preis/Einheit" sortKey="preis_pro_einheit" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Lieferant" sortKey="lieferant" sortConfig={sortConfig} onSort={setSortConfig} />
                <th className="text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {sortData(zutaten, sortConfig).map((zutat) => (
                <tr key={zutat.id} data-testid={`zutat-row-${zutat.id}`}>
                  <td className="font-body font-medium">{zutat.name}</td>
                  <td>
                    <span className="badge badge-neutral">{zutat.einheit}</span>
                  </td>
                  <td>{formatCurrency(zutat.preis_pro_einheit)}</td>
                  <td className="text-[#858585]">{zutat.lieferant || "-"}</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-2">
                      <button 
                        className="btn-icon"
                        onClick={() => openEditDialog(zutat)}
                        data-testid={`edit-zutat-${zutat.id}`}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button 
                        className="btn-icon danger"
                        onClick={() => handleDelete(zutat.id)}
                        data-testid={`delete-zutat-${zutat.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingZutat ? "Zutat bearbeiten" : "Neue Zutat erstellen"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="z.B. Rinderfilet"
                  className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39] font-mono"
                  data-testid="zutat-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Einheit</Label>
                  <Select 
                    value={formData.einheit} 
                    onValueChange={(value) => setFormData({...formData, einheit: value})}
                  >
                    <SelectTrigger className="border-0 border-b border-stone-300 rounded-none px-0 focus:ring-0" data-testid="zutat-einheit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EINHEITEN.map((einheit) => (
                        <SelectItem key={einheit} value={einheit}>{einheit}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Preis/Einheit (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.preis_pro_einheit}
                    onChange={(e) => setFormData({...formData, preis_pro_einheit: e.target.value})}
                    placeholder="0.00"
                    className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39] font-mono"
                    data-testid="zutat-preis-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Lieferant</Label>
                <Input
                  value={formData.lieferant}
                  onChange={(e) => setFormData({...formData, lieferant: e.target.value})}
                  placeholder="z.B. METRO, Großhändler XY"
                  className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39] font-mono"
                  data-testid="zutat-lieferant-input"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
              <Button 
                type="button" 
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="rounded-sm"
                data-testid="cancel-zutat-btn"
              >
                Abbrechen
              </Button>
              <Button 
                type="submit"
                className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm btn-press"
                data-testid="save-zutat-btn"
              >
                <Check size={16} className="mr-2" />
                {editingZutat ? "Aktualisieren" : "Erstellen"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Zutaten;
